import { tEn, tZh } from '../i18n';
import type { DisposableScreen, Spread } from '../types';
import { escapeHtml } from './escape';

const MIN_DURATION_MS = 3000;
const MAX_DURATION_MS = 5000;

/**
 * Mounts the breath screen. Faithful port of the v4 mockup race:
 * `Promise.all([minBreath, Promise.race([ai, maxBreath])])`.
 *
 * AI keeps loading in the background after advance — reveal's per-card
 * skeleton + 深海未醒 fallback handle any remaining wait.
 *
 * Lifecycle: dispose before advance aborts the AC; dispose after advance
 * keeps the AC alive (reveal owns it).
 *
 * The spread is rendered as a quiet intro at the top of the screen so the
 * user can read what each card slot will represent while the breath plays.
 */
export function mountBreath(
  ready: Promise<unknown>,
  ac: AbortController,
  spread: Spread,
  onContinue: () => void,
): HTMLElement {
  const screen = document.createElement('section');
  screen.id = 'screen-breath';
  screen.className = 'screen active';
  const positionsHtml = spread.positions
    .map(
      (p, i) => `<li>
        <span class="bp-num">${String(i + 1).padStart(2, '0')}</span>
        <span lang="en"><em>${escapeHtml(p.label_en)}</em></span>
        <span lang="zh">${escapeHtml(p.label_zh)}</span>
      </li>`,
    )
    .join('');
  screen.innerHTML = `
    <div class="breath-intro">
      <div class="bi-name">
        <span lang="en"><em>${escapeHtml(spread.name_en)}</em></span>
        <span lang="zh">${escapeHtml(spread.name_zh)}</span>
      </div>
      <div class="bi-good">
        <span lang="en">${escapeHtml(spread.good_for_en)}</span>
        <span lang="zh">${escapeHtml(spread.good_for_zh)}</span>
      </div>
      <ol class="bi-positions">${positionsHtml}</ol>
    </div>
    <div class="breath-stage">
      <div class="b-ring"></div>
      <div class="b-ring"></div>
      <div class="b-ring"></div>
      <div class="b-core"></div>
    </div>
    <div class="breath-text">
      <span lang="en"><em>${tEn('breath.line')}</em></span>
      <span lang="zh">${tZh('breath.line')}</span>
    </div>
    <div class="breath-hint" id="breath-hint">
      <span lang="en"><em>${tEn('breath.hint_skip')}</em></span>
      <span lang="zh">${tZh('breath.hint_skip')}</span>
    </div>
  `;

  let advanced = false;
  let disposed = false;
  // Mirror of minBreath for the synchronous click handler — promise can't
  // be polled, but a click reads the boolean directly.
  let minElapsed = false;
  const timers: ReturnType<typeof setTimeout>[] = [];

  const advance = (): void => {
    if (advanced || disposed) return;
    advanced = true;
    onContinue();
  };

  const delay = (ms: number): Promise<void> => new Promise((r) => timers.push(setTimeout(r, ms)));

  const minBreath = delay(MIN_DURATION_MS).then(() => {
    minElapsed = true;
    // Reveal the "tap anywhere to continue" hint at the 3s mark so the
    // skip-affordance becomes visible exactly when click-skip is armed.
    screen.querySelector<HTMLElement>('#breath-hint')?.classList.add('ready');
  });
  const maxBreath = delay(MAX_DURATION_MS);
  // Treat reject as settled so the race always resolves cleanly even if AI errors.
  const aiOrError = ready.then(
    () => undefined,
    () => undefined,
  );
  Promise.all([minBreath, Promise.race([aiOrError, maxBreath])])
    .then(advance)
    .catch((err) => console.error('breath advance failed', err));

  // Click advances early — only after the 3s contemplation floor.
  screen.addEventListener('click', () => {
    if (minElapsed) advance();
  });

  (screen as DisposableScreen).dispose = () => {
    disposed = true;
    for (const t of timers) clearTimeout(t);
    if (!advanced) ac.abort();
  };

  return screen;
}
